import React, { useMemo } from "react";
import type {
  CryptoAlertData,
  PositionData,
  UserPosition,
} from "@crypto-trading/common/browser";
import { ChevronRight } from "lucide-react";
import { ResponsiveDrawer } from "@/components/ResponsiveDrawer";
import { useTokenLivePrice } from "@/hooks/useTokenLivePrice";
import {
  alertPositions,
  exchangeLabel,
  isOpenPosition,
  pickBestPosition,
  positionPnlPercent,
  toDrawerPosition,
} from "@/lib/alert-positions";

interface AlertPositionsDrawerProps {
  alert: CryptoAlertData | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectPosition: (position: PositionData) => void;
  onTrade?: (alert: CryptoAlertData) => void;
  /** Set when opened from inside another mobile drawer. */
  nested?: boolean;
}

const formatPrice = (price: number | null | undefined) => {
  if (!price) return "—";
  return `$${price.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  })}`;
};

const formatTime = (date: string | Date | null) => {
  if (!date) return "";
  return new Date(date).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const pnlColor = (value: number | null) => {
  if (value === null || value === 0) return "text-text-primary";
  return value > 0 ? "text-green-400" : "text-red-400";
};

export const AlertPositionsDrawer: React.FC<AlertPositionsDrawerProps> = ({
  alert,
  open,
  onOpenChange,
  onSelectPosition,
  onTrade,
  nested = false,
}) => {
  const positions = useMemo(
    () => (alert ? alertPositions(alert) : []),
    [alert]
  );
  const hasOpenPosition = positions.some(isOpenPosition);

  const { price: livePrice } = useTokenLivePrice(
    alert?.currency,
    undefined,
    open && hasOpenPosition
  );

  const sorted = useMemo(
    () =>
      [...positions].sort(
        (a, b) =>
          (positionPnlPercent(b, livePrice) ?? 0) -
          (positionPnlPercent(a, livePrice) ?? 0)
      ),
    [positions, livePrice]
  );
  const best = pickBestPosition(positions, livePrice);

  if (!alert) return null;

  const footer = onTrade ? (
    <button
      type="button"
      onClick={() => onTrade(alert)}
      className="w-full py-2.5 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors text-sm font-medium"
    >
      Trade {alert.currency}
    </button>
  ) : undefined;

  return (
    <ResponsiveDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={`${alert.currency} positions`}
      footer={footer}
      nested={nested}
      zIndex={nested ? 110 : 100}
    >
      <div className="p-4 space-y-2">
        {sorted.length === 0 && (
          <p className="text-sm text-text-secondary text-center py-8">
            You have no positions for this alert yet.
          </p>
        )}
        {sorted.map((position) => (
          <PositionRow
            key={position.id}
            position={position}
            pnlPercent={positionPnlPercent(position, livePrice)}
            isBest={sorted.length > 1 && position.id === best?.id}
            onClick={() => onSelectPosition(toDrawerPosition(position))}
          />
        ))}
      </div>
    </ResponsiveDrawer>
  );
};

const PositionRow: React.FC<{
  position: UserPosition;
  pnlPercent: number | null;
  isBest: boolean;
  onClick: () => void;
}> = ({ position, pnlPercent, isBest, onClick }) => {
  const isOpen = isOpenPosition(position);

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left bg-primary/30 rounded-xl p-3 border border-border/50 flex items-center justify-between gap-3 hover:border-accent/40 transition-colors"
    >
      <div className="min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-text-primary">
            {exchangeLabel(position)}
          </span>
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              position.positionType === "SHORT"
                ? "bg-red-500/15 text-red-400"
                : "bg-green-500/15 text-green-400"
            }`}
          >
            {position.positionType === "SHORT" ? "Short" : "Long"}
          </span>
          <span className="text-xs text-text-secondary">
            {position.leverage}x
          </span>
          <span
            className={`px-2 py-0.5 rounded-full text-xs ${
              isOpen
                ? "bg-accent/15 text-accent"
                : "bg-gray-600/30 text-text-secondary"
            }`}
          >
            {isOpen ? "Open" : "Closed"}
          </span>
          {isBest && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-500/15 text-yellow-400">
              Best
            </span>
          )}
        </div>
        <p className="text-xs text-text-secondary">
          Entry {formatPrice(position.entryPrice)}
          {!isOpen && ` · Exit ${formatPrice(position.exitPrice)}`}
        </p>
        <p className="text-xs text-text-secondary">
          Opened {formatTime(position.openedAt)}
          {!isOpen && position.closedAt
            ? ` · Closed ${formatTime(position.closedAt)}`
            : ""}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <div className="text-right">
          <p className={`text-sm font-medium ${pnlColor(pnlPercent)}`}>
            {pnlPercent === null
              ? "—"
              : `${pnlPercent >= 0 ? "+" : ""}${pnlPercent.toFixed(2)}%`}
          </p>
          <p className="text-xs text-text-secondary">
            ${position.usdAmount.toFixed(2)}
          </p>
        </div>
        <ChevronRight className="w-4 h-4 text-text-secondary" />
      </div>
    </button>
  );
};
